import fetcher from '../lib/fetcher'
import {TGroupActivityData} from '../types/activity.types'
import {DemandFormType} from '../types/demand.type'
import {
  TActivity,
  TActivitiesResponse,
  TNetworkActivitiesFilters,
} from '../types/project.type'

/**
 * Response of PATCH /api/activity/:id — the updated activity plus the number of
 * coactivity authorizations the update invalidated server-side (delete-on-update).
 */
export type TUpdateActivityResponse = TActivity & {
  invalidatedCoactivityCount?: number
}

const updateActivities = async (
  activities: TActivity[],
): Promise<(TUpdateActivityResponse | undefined)[]> => {
  return Promise.all(
    activities.map(async activity => {
      const response = await fetcher.patch<TActivity, TUpdateActivityResponse>(
        `/api/activity/${activity.id}`,
        activity,
      )
      return response.data
    }),
  )
}

/**
 * Fetch a single activity by id in the enhanced shape (zones, protections,
 * projects[], canEdit/canDelete) consumed by ActivityPopup.
 */
const getActivityById = async (activityId: string): Promise<TActivity> => {
  const response = await fetcher.get<TActivity>(`/api/activity/${activityId}`)
  if (!response.data) {
    throw new Error('Activity not found')
  }
  return response.data
}

const approveWeek = async (
  weekNumber: string,
  year: string,
  networkId: string,
  userId: string,
) => {
  const response = await fetcher.post(`/api/activity/approve-week`, {
    weekNumber,
    year,
    networkId,
    userId,
  })
  return response.data
}

const inProgressWeek = async (
  weekNumber: string,
  year: string,
  networkId: string,
  userId: string,
) => {
  const response = await fetcher.post(`/api/activity/inprogress-week`, {
    weekNumber,
    year,
    networkId,
    userId,
  })
  return response.data
}

const approveDay = async (date: string, networkId: string, userId: string) => {
  const response = await fetcher.post(`/api/activity/approve-day`, {
    date,
    networkId,
    userId,
  })
  return response.data
}

const inProgressDay = async (
  date: string,
  networkId: string,
  userId: string,
) => {
  const response = await fetcher.post(`/api/activity/in-progress-day`, {
    date,
    networkId,
    userId,
  })
  return response.data
}

/**
 * Approve a single activity by id
 * @param activityId
 * @returns The approved activity
 */
const approveActivityById = async (activityId: string): Promise<TActivity> => {
  const response = await fetcher.patch<undefined, TActivity>(
    `/api/activity/${activityId}/approve`,
  )
  if (!response.data) {
    throw new Error('Failed to approve activity')
  }
  return response.data
}

/**
 * Put an activity in progress (pending) by id
 * @param activityId
 * @returns The updated activity
 */
const inProgressActivityById = async (
  activityId: string,
): Promise<TActivity> => {
  const response = await fetcher.patch<undefined, TActivity>(
    `/api/activity/${activityId}/in-progress`,
  )
  if (!response.data) {
    throw new Error('Failed to change activity status to in-progress')
  }
  return response.data
}

/**
 * Hard delete activity by id (permanently removes from database)
 * @param activityId
 * @returns True if the activity was deleted
 */
const hardDeleteActivity = async (activityId: string) => {
  const response = await fetcher.del(`/api/activity/hard-delete/${activityId}`)
  return response.data
}

/**
 * Soft delete activity by id (marks as deleted but keeps in database)
 * @param activityId
 * @returns True if the activity was soft deleted
 */
const softDeleteActivity = async (activityId: string) => {
  const response = await fetcher.del(`/api/activity/soft-delete/${activityId}`)
  return response.data
}

/**
 * Create activities in batch
 * @param formData - Form data with sections[], workingDays[], and other activity fields
 * @param networkId
 * @returns Array of activities
 *
 * Format optimisé: Le backend crée workingDays.length activités,
 * chacune avec sections.length sections
 */
const batchCreateActivities = async (
  formData: DemandFormType,
  networkId: string,
) => {
  const {workingDays, sections, ...baseData} = formData
  const response = await fetcher.post('/api/activity/batch-create', {
    ...baseData,
    networkId,
    workingDays,
    sections,
  })
  return response.data
}

/**
 * Update group activities by id, returns the new activities.
 * The API will delete old activities and create new one in replacement.
 * @param formData
 * @returns Array of activities
 */
const batchUpdateGroupActivities = async (formData: DemandFormType) => {
  const response = await fetcher.post(
    '/api/activity/batch-update-group',
    formData,
  )
  return response.data
}

/**
 * Update the given occurrence and every following one in its group.
 *
 * The scope (occurrences on or after this activity's date) is derived
 * server-side from the activity id — it is never sent by the client. Earlier
 * occurrences are invisible to the update and survive it untouched.
 * @param activityId - The pivot occurrence the scope starts at
 * @param formData
 * @returns Array of the created + updated activities
 */
const batchUpdateFollowingGroupActivities = async (
  activityId: string,
  formData: DemandFormType,
) => {
  const response = await fetcher.patch(
    `/api/activity/${activityId}/following`,
    formData,
  )
  return response.data
}

/**
 * Promotes a solo activity to a group activity.
 * Preserves the original activity id and history.
 * @param activityId - ID of the solo activity to promote
 * @param formData - Group form data (workingDays, sections, common fields)
 * @returns Array of all activities in the new group
 */
const promoteToGroup = async (
  activityId: string,
  formData: DemandFormType,
): Promise<TActivity[]> => {
  const response = await fetcher.post<DemandFormType, TActivity[]>(
    `/api/activity/${activityId}/promote-to-group`,
    formData,
  )
  return response.data as TActivity[]
}

/**
 * Get group activity by id, returns only the fields id, date, trackId and status of the activities in the group
 * @param activityGroupId
 * @returns Object with an array of activities (only the fields id, date, trackId and status)
 */
const getGroupActivity = async (
  activityGroupId: string,
): Promise<TGroupActivityData> => {
  const response = await fetcher.get<TGroupActivityData>(
    `/api/activity/group-activity/${activityGroupId}`,
  )
  if (!response.data) {
    throw new Error('Group activity not found')
  }
  return response.data
}

/**
 * Soft delete all activities in the group (status is set to 'deleted')
 * @param activityGroupId
 * @returns True if the activities were soft deleted
 */
const batchSoftDeleteGroupActivities = async (activityGroupId: string) => {
  const response = await fetcher.post('/api/activity/batch-soft-delete', {
    activityGroupId,
  })
  return response.data
}

/**
 * Hard delete all activities in the group (hard delete)
 * @param activityGroupId
 * @returns True if the activities were hard deleted
 */
const batchHardDeleteGroupActivities = async (activityGroupId: string) => {
  const response = await fetcher.delWithBody(
    '/api/activity/batch-hard-delete',
    {activityGroupId},
  )
  return response.data
}

const buildActivityQueryParams = (
  filters?: TNetworkActivitiesFilters,
  page: number = 1,
  perPage?: number,
): URLSearchParams => {
  const params = new URLSearchParams()
  params.append('page', page.toString())
  // Omit perPage to let the API apply its default page size.
  if (perPage !== undefined) params.append('perPage', perPage.toString())

  if (filters?.date) params.append('date', filters.date)
  if (filters?.from) params.append('from', filters.from)
  if (filters?.to) params.append('to', filters.to)
  if (filters?.weekNumber)
    params.append('weekNumber', filters.weekNumber.toString())
  if (filters?.year) params.append('year', filters.year.toString())
  if (filters?.trackIds && filters.trackIds.length > 0) {
    params.append('trackIds', filters.trackIds.join(','))
  }
  if (filters?.sectorIds && filters.sectorIds.length > 0) {
    params.append('sectorIds', filters.sectorIds.join(','))
  }
  if (filters?.protectionModeIds && filters.protectionModeIds.length > 0) {
    params.append('protectionModeIds', filters.protectionModeIds.join(','))
  }
  if (filters?.shift) params.append('shift', filters.shift)
  if (filters?.teamIds && filters.teamIds.length > 0) {
    params.append('teamIds', filters.teamIds.join(','))
  }
  if (filters?.requestorIds && filters.requestorIds.length > 0) {
    params.append('requestorIds', filters.requestorIds.join(','))
  }
  if (filters?.activityTypes && filters.activityTypes.length > 0) {
    params.append('activityTypes', filters.activityTypes.join(','))
  }
  if (filters?.statuses && filters.statuses.length > 0) {
    params.append('statuses', filters.statuses.join(','))
  }
  if (filters?.sortBy) params.append('sortBy', filters.sortBy)
  if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder)
  // Active only alongside sortBy === 'protections' — the API ignores it
  // otherwise (see GET /activities contract).
  if (filters?.sortProtectionTypeId)
    params.append('sortProtectionTypeId', filters.sortProtectionTypeId)
  if (filters?.search && filters.search.trim().length > 0)
    params.append('search', filters.search.trim())
  if (filters?.entry && filters.entry.trim().length > 0)
    params.append('entry', filters.entry.trim())

  return params
}

// Get activities for a network.
const getNetworkActivities = async (
  networkId: string,
  filters?: TNetworkActivitiesFilters,
  page: number = 1,
  perPage?: number,
): Promise<TActivitiesResponse> => {
  const params = buildActivityQueryParams(filters, page, perPage)

  const response = await fetcher.get<TActivitiesResponse>(
    `/api/network/${networkId}/activities?${params.toString()}`,
  )

  if (!response.data) {
    throw new Error('Failed to get network activities')
  }

  return response.data
}

/**
 * Reconcile one run of an activity's sections on a track from its new PK
 * envelope. `sectionIds` scope the server reconcile to the run, so sibling
 * sections on the same track are untouched; the API re-resolves the path and
 * patches/creates/deletes atomically — no per-section geometry is sent.
 * @returns The complete updated activity
 */
const updateActivityTrackRun = async (payload: {
  activityId: string
  networkTrackId: string
  pkStart: number
  pkEnd: number
  sectionIds: string[]
}): Promise<TActivity> => {
  const response = await fetcher.patch<typeof payload, TActivity>(
    '/api/activitySection/trackRun',
    payload,
  )

  if (!response.data) {
    throw new Error('Failed to update activity sections')
  }

  return response.data
}

/**
 * Toggle the operational "live" flag of an activity.
 * Idempotent: the boolean is encoded in the querystring (active=true|false).
 */
const setActivityLive = async (activityId: string, active: boolean) => {
  const params = new URLSearchParams()
  params.set('active', String(active))
  const response = await fetcher.patch<undefined, TActivity>(
    `/api/activity/${activityId}/live?${params.toString()}`,
  )
  return response.data
}

const updateActivityPriorityFields = async (
  activityId: string,
  patch: {
    priority?: number | null
    entryTrackIds?: string[]
    exitTrackIds?: string[]
  },
) => {
  // The API's UpdateActivitySchema requires `id` in the body (legacy contract;
  // a path-only refactor is tracked as a separate plan). We forward the path
  // id into the body so the Zod parse passes.
  const response = await fetcher.patch<
    {
      id: string
      priority?: number | null
      entryTrackIds?: string[]
      exitTrackIds?: string[]
    },
    TActivity
  >(`/api/activity/${activityId}`, {id: activityId, ...patch})
  return response.data
}

const ActivityService = {
  updateActivities,
  getActivityById,
  approveActivityById,
  inProgressActivityById,
  approveWeek,
  approveDay,
  inProgressDay,
  inProgressWeek,
  hardDeleteActivity,
  softDeleteActivity,
  batchCreateActivities,
  batchUpdateGroupActivities,
  batchUpdateFollowingGroupActivities,
  promoteToGroup,
  getGroupActivity,
  batchSoftDeleteGroupActivities,
  batchHardDeleteGroupActivities,
  getNetworkActivities,
  updateActivityTrackRun,
  updateActivityPriorityFields,
  setActivityLive,
}

export default ActivityService
